import React, { useState, useEffect, useContext } from "react";
import "./paymentN.css";
import { PopupContext } from "../../PopupContext";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Copy,
  Check,
  ChevronRight,
  Upload,
  Send,
  Palette,
  QrCode,
  X,
} from "lucide-react";
import axios from "axios";
import { createPortal } from "react-dom";

const Payment = ({
  isOpen = false,
  onClose = () => {},
  packName,
  withoutIphone = false,
}) => {
  const [details, setDetails] = useState(null);
  const userId = localStorage.getItem("userId");
  const userEmail = localStorage.getItem("userEmail");
  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const { closeAllPopups } = useContext(PopupContext);

  const [isCopied, setIsCopied] = useState(false);
  const [popupState, setPopupState] = useState(null);
  const [paymentType, setPaymentType] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedMethodDetails, setSelectedMethodDetails] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [quantity, setQuantity] = useState(1);
  const [receiptPhoto, setReceiptPhoto] = useState(null);
  const [receiptName, setReceiptName] = useState("Upload receipt image");
  const [showQR, setShowQR] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);

  // A "without iPhone" price is available only if configured in the pack.
  const hasWithoutIphone = Number(details?.price_Without_Iphone) > 0;
  const isWithoutIphone = Boolean(withoutIphone && hasWithoutIphone);

  const iphoneColors = details?.iphoneColors || [];
  const needsColor = !isWithoutIphone && iphoneColors.length > 0;
  const selectedColorObj = iphoneColors.find((c) => c.name === selectedColor);

  // Determine the fee from the pack type returned by the API.
  // PackSchema uses exactly "normal" and "VIP".
  const actualPackType = details?.packType === "VIP" ? "VIP" : "normal";
  const subscriptionFee = actualPackType === "VIP" ? 50 : 5;

  // Base price is only for display. The backend calculates the final price.
  const basePrice = details
    ? Number(isWithoutIphone ? details.price_Without_Iphone : details.price)
    : 0;

  const unitPrice =
    details && Number.isFinite(basePrice) ? basePrice + subscriptionFee : 0;
  const totalPrice = unitPrice * quantity;

  // National payment methods only.
  const availableMethods = [
    { value: "poste", label: "Tunisian Post", image: "poste.jpg" },
    { value: "d17", label: "D17", image: "D17.JPG" },
    { value: "izi", label: "IZI", image: "izi.jpeg" },
    { value: "flouci", label: "Flouci", image: "flouci.jpg" },
  ];

  const methodDetails = {
    poste: {
      agency: "PosteBank",
      number: "5359401735312676",
      ribLabel: "e-dinar RIB",
    },
    d17: {
      agency: "PosteBank",
      phone: "53314233",
      qrCode: "/images/payment/QR-CodeD17.jpg",
    },
    izi: {
      agency: "IZI",
      number: "81001000014421205555",
      phone: "53314233",
      qrCode: "/images/payment/QR-CodeIZI.jpg",
    },
    flouci: {
      agency: "Flouci",
      number: "04050135007891671539",
      phone: "53314233",
      qrCode: "/images/payment/flouciQR.jpg",
    },
  };

  const formatPrice = (price) =>
    price == null || !Number.isFinite(Number(price))
      ? "0.000"
      : Number(price).toFixed(3);

  // Fetch pack information from the backend.
  useEffect(() => {
    let cancelled = false;

    const fetchPackDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${API_URL}/packs/${encodeURIComponent(packName)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!cancelled) setDetails(response.data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.message || "Failed to fetch pack details."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (packName) fetchPackDetails();
    else setLoading(false);

    return () => {
      cancelled = true;
    };
  }, [API_URL, packName, token]);

  // If the selected pack does not include an iPhone, clear the color.
  useEffect(() => {
    if (!needsColor && selectedColor) setSelectedColor(null);
  }, [needsColor, selectedColor]);

  // Payment facilities are available only for the full-price version.
  useEffect(() => {
    if (isWithoutIphone && paymentType === "facility") {
      setPaymentType(null);
      setSelectedFacility(null);
    }
  }, [isWithoutIphone, paymentType]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file.");
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB.");
      e.target.value = "";
      return;
    }

    setReceiptPhoto(file);
    setReceiptName(file.name);
  };

  const handleSubmit = async () => {
    if (!userId || !userEmail) {
      toast.error("User ID or Email is missing.");
      return;
    }

    if (!details || !Number.isFinite(basePrice) || basePrice < 0) {
      toast.error("Invalid pack price.");
      return;
    }

    if (!selectedMethod || !paymentType || !quantity || !receiptPhoto) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (paymentType === "facility" && !selectedFacility) {
      toast.error("Please select a payment period.");
      return;
    }

    if (needsColor && !selectedColor) {
      toast.error("Please choose an iPhone color.");
      return;
    }

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("userEmail", userEmail);
    formData.append("packName", packName);
    // Do not send price/totalPrice as authoritative values:
    // the backend must calculate them from the Pack document.
    formData.append("quantity", quantity.toString());
    formData.append("paymentType", paymentType);
    formData.append("paymentMethod", selectedMethod);
    formData.append("withoutIphone", String(isWithoutIphone));

    if (needsColor && selectedColor) {
      formData.append("iphoneColor", selectedColor);
    }

    formData.append("receiptPhoto", receiptPhoto);

    if (paymentType === "facility" && selectedFacility) {
      const facility = details.paymentFacilities?.find(
        (f) => f.period === selectedFacility
      );

      if (facility) {
        formData.append("paymentFacilities", JSON.stringify(facility));
      }
    }

    try {
      const response = await fetch(`${API_URL}/payments`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || "Payment successfully recorded.", {
          position: "top-center",
          autoClose: 3000,
          style: {
            backgroundColor: "rgba(60,60,81,0.4)",
            color: "white",
            fontSize: "13px",
            borderRadius: "8px",
            padding: "10px",
          },
        });

        onClose();
        closeAllPopups();
      } else {
        toast.error(data.message || "Error processing payment");
      }
    } catch (err) {
      toast.error("Error: " + err.message);
    }
  };

  const togglePaymentTypePopup = () =>
    setPopupState(popupState ? null : "paymentType");

  const handlePaymentTypeSelect = (type) => {
    setPaymentType(type);
    if (type === "facility") setPopupState("facilityPeriods");
    else setPopupState(null);
  };

  const handleBack = () => {
    if (popupState === "methodDetails") setPopupState("methodList");
    else if (popupState === "methodList") setPopupState(null);
    else if (popupState === "facilityAmount") setPopupState("facilityPeriods");
    else if (popupState === "facilityPeriods") setPopupState("paymentType");
    else setPopupState(null);
  };

  const openMethodList = () => setPopupState("methodList");
  const openColorList = () => setPopupState("colorList");

  const openMethodDetails = (method) => {
    setSelectedMethodDetails(methodDetails[method.value] || {});
    setSelectedMethod(method.value);
    setIsCopied(false);
    setShowQR(false);
    setPopupState("methodDetails");
  };

  const handleSingleCheckboxChange = (_e, value) => {
    handlePaymentTypeSelect(value);
  };

  const copyValue = (value, label) => {
    navigator.clipboard.writeText(value);
    setIsCopied(true);
    toast.success(`${label} copied!`, { autoClose: 1200 });
    setTimeout(() => setIsCopied(false), 1500);
  };

  if (!isOpen) return null;

  const popupContent = popupState && (
    <div className="infoOverlayN" onClick={handleBack}>
      <div className="infoModalN" onClick={(e) => e.stopPropagation()}>
        <ArrowLeft className="close_icon" onClick={handleBack} />

        {/* PAYMENT METHOD LIST (national only) */}
        {popupState === "methodList" && (
          <div className="method-grid">
            {availableMethods.map((method) => (
              <p
                key={method.value}
                className={selectedMethod === method.value ? "selected" : ""}
                onClick={() => openMethodDetails(method)}
              >
                <img
                  src={`/images/payment/${method.image}`}
                  alt={method.label}
                  className="icon1"
                />
                {method.label}
              </p>
            ))}
          </div>
        )}

        {/* IPHONE COLOR LIST */}
        {popupState === "colorList" && (
          <>
            {details?.iphoneModel && (
              <div className="color-sheet-title">{details.iphoneModel}</div>
            )}
            <div className="color-grid">
              {iphoneColors.map((color) => (
                <p
                  key={color.name}
                  className={selectedColor === color.name ? "selected" : ""}
                  onClick={() => {
                    setSelectedColor(color.name);
                    setPopupState(null);
                  }}
                >
                  <span
                    className="color-swatch"
                    style={{ background: color.hex }}
                  />
                  {color.name}
                </p>
              ))}
            </div>
          </>
        )}

        {/* PAYMENT METHOD DETAILS */}
        {popupState === "methodDetails" && selectedMethodDetails && (
          <div className="simple-details">
            {selectedMethodDetails.agency && (
              <div className="simple-line">
                <span className="simple-title">Agency</span>
                <span className="simple-info rib-line">
                  {selectedMethodDetails.agency}
                </span>
              </div>
            )}

            {selectedMethodDetails.number && (
              <div className="simple-line">
                <span className="simple-title">
                  {selectedMethodDetails.ribLabel || "RIB"}
                </span>
                <span className="simple-info rib-line">
                  <span className="rib-text">
                    {selectedMethodDetails.number}
                  </span>
                  <span
                    className="copy-icon"
                    onClick={() =>
                      copyValue(
                        selectedMethodDetails.number,
                        selectedMethodDetails.ribLabel || "RIB"
                      )
                    }
                  >
                    {isCopied ? <Check size={12} /> : <Copy size={12} />}
                  </span>
                </span>
              </div>
            )}

            {selectedMethodDetails.phone && (
              <div className="simple-line">
                <span className="simple-title">Contact</span>
                <span className="simple-info rib-line">
                  {selectedMethodDetails.phone}
                </span>
              </div>
            )}

            {selectedMethodDetails.qrCode && (
              <div
                className="simple-line qr-line"
                onClick={() => setShowQR(true)}
              >
                <span className="simple-title">QR Code</span>
                <span className="simple-info qr-hint">
                  <QrCode size={14} />
                  Tap to view
                  <ChevronRight size={13} />
                </span>
              </div>
            )}

            <div className="simple-footer">
              <button
                type="button"
                className="simple-btn"
                onClick={() => {
                  setPopupState(null);
                  toast.success(`${selectedMethod.toUpperCase()} selected`);
                }}
              >
                Confirm
              </button>
            </div>

            {showQR &&
              selectedMethodDetails.qrCode &&
              createPortal(
                <div
                  className="qrx-overlay"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowQR(false);
                  }}
                >
                  <div className="qrx-card" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="qrx-close"
                      onClick={() => setShowQR(false)}
                      aria-label="Close"
                    >
                      <X size={14} />
                    </button>

                    <div className="qrx-title">Scan to pay</div>

                    <div className="qrx-frame">
                      <img
                        src={selectedMethodDetails.qrCode}
                        alt="QR Code"
                        className="qrx-img"
                      />
                    </div>

                    <div className="qrx-badge">
                      {selectedMethod?.toUpperCase()}
                    </div>
                    <div className="qrx-hint">
                      Open the app and scan this code
                    </div>
                  </div>
                </div>,
                document.body
              )}
          </div>
        )}

        {/* PAYMENT TYPE */}
        {popupState === "paymentType" && (
          <>
            {details?.paymentFacilities?.length > 0 && !isWithoutIphone && (
              <p>
                <img
                  src="/images/payment/facilite.jpg"
                  alt="payment facility"
                  className="icon1"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                Payment facility
                <input
                  type="checkbox"
                  value="facility"
                  className="checkTypePayment"
                  checked={paymentType === "facility"}
                  onChange={(e) => handleSingleCheckboxChange(e, "facility")}
                />
              </p>
            )}

            <p>
              <img
                src="/images/payment/pay_cache.jpg"
                alt="cash"
                className="icon1"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              Cash payment
              <input
                type="checkbox"
                value="cash"
                className="checkTypePayment"
                checked={paymentType === "cash"}
                onChange={(e) => handleSingleCheckboxChange(e, "cash")}
              />
            </p>
          </>
        )}

        {/* FACILITY PERIODS */}
        {popupState === "facilityPeriods" &&
          details?.paymentFacilities?.length > 0 &&
          details.paymentFacilities.map((facility) => (
            <p key={facility.period}>
              <img
                src={`/images/payment/${facility.period.replace(" ", "")}.jpg`}
                alt={facility.period}
                className="icon1"
                onError={(e) => {
                  e.currentTarget.src = "/images/payment/date.png";
                }}
              />
              Payment period {facility.period}
              <input
                type="checkbox"
                value={facility.period}
                className="checkTypePaymentFacility"
                checked={selectedFacility === facility.period}
                onChange={() => {
                  setSelectedFacility(facility.period);
                  setPopupState("facilityAmount");
                }}
              />
            </p>
          ))}

        {/* FACILITY AMOUNT */}
        {popupState === "facilityAmount" && selectedFacility && (
          <p style={{ cursor: "default" }}>
            <img
              src="/images/payment/calcule.png"
              alt="calculation"
              className="icon1"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            {(() => {
              const facility = details.paymentFacilities.find(
                (f) => f.period === selectedFacility
              );
              return facility
                ? `${formatPrice(facility.monthlyAmount)} TND × ${facility.period} = ${formatPrice(facility.totalAmount)} TND`
                : "Invalid";
            })()}
          </p>
        )}
      </div>
    </div>
  );

  const getPackImage = (name) => {
    const normalizedName = (name || "")
      .trim()
      .replace(/^iPhone\s*/i, "M")
      .replace(/\s+/g, "_");

    return `/images/packs/unlimitedBonus/${normalizedName}.png`;
  };

  return (
    <div className="details-container-payment">
      {loading ? (
        <p style={{ justifyContent: "center", cursor: "default" }}>
          Loading…
        </p>
      ) : error ? (
        <p
          style={{
            justifyContent: "center",
            color: "#f09595",
            cursor: "default",
          }}
        >
          {error}
        </p>
      ) : (
        <>
          {/* PACK HEADER */}
          <div className="pay-header">
            <img
              src={getPackImage(packName)}
              alt={packName}
              className="pay-header-thumb"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src =
                  "/images/packs/unlimitedBonus/pack_N.jpg";
              }}
            />

            <div className="pay-header-info">
              <div className="pay-header-name">{packName}</div>
              <div className="pay-header-sub">
                National
                {isWithoutIphone ? " · Without iPhone" : ""}
                {actualPackType === "VIP" ? " · VIP" : ""}
              </div>
            </div>

            <div className="pay-header-price">
              {formatPrice(unitPrice)} TND
            </div>
          </div>

          {/* QUANTITY */}
          <div className="pay-section-label">Quantity</div>
          <div className="pay-qty-row">
            <span className="pay-qty-label">Number of packs</span>

            <div
              className={`pay-qty-pill ${
                quantity === 1 ? "active" : "inactive"
              }`}
              onClick={() => setQuantity(1)}
            >
              1
            </div>

            <div
              className={`pay-qty-pill ${
                quantity === 2 ? "active" : "inactive"
              }`}
              onClick={() => setQuantity(2)}
            >
              2
            </div>

            <span className="pay-qty-total">
              = <span>{formatPrice(totalPrice)} TND</span>
            </span>
          </div>

          {/* PAYMENT */}
          <div className="pay-section-label">Payment</div>

          <p onClick={togglePaymentTypePopup}>
            <div className="pay-row-icon amber">
              <img
                src="/images/payment/type.jpg"
                alt="type"
                className="icon1"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
            <div className="pay-row-text">
              <div className="pay-row-title">Type of payment</div>
              <div className="pay-row-sub">
                {paymentType
                  ? paymentType.charAt(0).toUpperCase() + paymentType.slice(1)
                  : "Not selected"}
              </div>
            </div>
            <ChevronRight size={14} className="pay-row-arrow" />
          </p>

          <p onClick={openMethodList}>
            <div className="pay-row-icon purple">
              <img
                src="/images/payment/methode.jpg"
                alt="method"
                className="icon1"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
            <div className="pay-row-text">
              <div className="pay-row-title">Payment method</div>
              <div className="pay-row-sub">
                {selectedMethod ? selectedMethod.toUpperCase() : "Not selected"}
              </div>
            </div>
            {selectedMethod ? (
              <span className="pay-method-badge">
                {selectedMethod.toUpperCase()}
              </span>
            ) : (
              <ChevronRight size={14} className="pay-row-arrow" />
            )}
          </p>

          {/* IPHONE COLOR */}
          {needsColor && (
            <p onClick={openColorList}>
              <div className="pay-row-icon blue">
                <Palette size={14} />
              </div>
              <div className="pay-row-text">
                <div className="pay-row-title">iPhone color</div>
                <div className="pay-row-sub">
                  {selectedColor || "Not selected"}
                </div>
              </div>
              {selectedColorObj ? (
                <span
                  className="color-swatch color-swatch-sm"
                  style={{ background: selectedColorObj.hex }}
                />
              ) : (
                <ChevronRight size={14} className="pay-row-arrow" />
              )}
            </p>
          )}

          {/* RECEIPT */}
          <div className="pay-section-label">Receipt</div>
          <label
            className={`pay-upload-zone ${receiptPhoto ? "has-file" : ""}`}
          >
            <div className="pay-upload-icon">
              <Upload
                size={16}
                color={receiptPhoto ? "#ffb84d" : "rgba(200,200,215,0.5)"}
              />
            </div>
            <div className="pay-upload-info">
              <div className="pay-upload-title">{receiptName}</div>
              {!receiptPhoto && (
                <div className="pay-upload-hint">JPG / PNG · max 5 MB</div>
              )}
            </div>
            <ChevronRight
              size={14}
              style={{ color: "rgba(200,200,215,0.3)", flexShrink: 0 }}
            />
            <input type="file" accept="image/*" onChange={handleFileChange} />
          </label>

          {/* SUBMIT */}
          <div className="packFooter">
            <button
              type="button"
              className="pay-submit-btn"
              onClick={handleSubmit}
            >
              <Send size={14} />
              Submit payment
            </button>
          </div>

          {popupContent}
        </>
      )}
    </div>
  );
};

export default Payment;